import { Typography, Box, Chip, Tooltip } from '@mui/material'

const BirdInfoSection = ({ 
  entry, 
  genderTerm, 
  ageDisplay, 
  varietyDisplay,
  currentWeight 
}) => {
    /**
   * Formats breed information for display, showing "Mix" with tooltip for multiple breeds
   * Breed: Main genetic grouping (e.g., "Rhode Island Red", "Wyandotte")
   * @returns {object} - Object with displayText and tooltipText
   */
  const getBreedDisplay = () => {
    if (!entry.breed) return { displayText: '', tooltipText: '' }
    
    // Handle case where breed is an array of multiple breeds
    if (Array.isArray(entry.breed)) {
      if (entry.breed.length === 0) return { displayText: '', tooltipText: '' }
      
      // Extract labels from objects or use strings directly
      const breedLabels = entry.breed.map(b => 
        typeof b === 'object' && b.label ? b.label : b
      ).filter(Boolean)
      
      if (breedLabels.length === 0) return { displayText: '', tooltipText: '' }
      if (breedLabels.length === 1) return { displayText: breedLabels[0], tooltipText: '' }
      return { 
        displayText: 'Mix', 
        tooltipText: breedLabels.join(', ') 
      }
    }
    
    // Handle case where breed is a string with comma-separated values
    if (typeof entry.breed === 'string') {
      const breeds = entry.breed.split(',').map(b => b.trim()).filter(b => b.length > 0)
      if (breeds.length === 0) return { displayText: '', tooltipText: '' }
      if (breeds.length === 1) return { displayText: breeds[0], tooltipText: '' }
      return { 
        displayText: 'Mix', 
        tooltipText: breeds.join(', ') 
      }
    }
    
    // Handle single object case
    if (typeof entry.breed === 'object' && entry.breed.label) {
      return { displayText: entry.breed.label, tooltipText: '' }
    }
    
    return { displayText: entry.breed.toString(), tooltipText: '' }
  }
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
        <Typography 
          variant="h6" 
          component="div"
          sx={{ 
            fontWeight: 700,
            fontSize: '1.1rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            mr: 1,
            color: 'text.primary'
          }}
        >
          {entry.name || `Entry ${entry.entryId.slice(0, 6)}`}
        </Typography>
        
        {/* Breed chip */}
        {getBreedDisplay().displayText && (
          getBreedDisplay().tooltipText ? (
            <Tooltip title={getBreedDisplay().tooltipText} arrow>
              <Chip 
                label={getBreedDisplay().displayText}
                size="small"
                variant="outlined"
                sx={{
                  cursor: 'help',
                  fontSize: '0.75rem',
                  height: 24,
                  '& .MuiChip-label': { px: 1 }
                }}
              />
            </Tooltip>
          ) : (
            <Chip 
              label={getBreedDisplay().displayText}
              size="small"
              variant="outlined"
              sx={{
                fontSize: '0.75rem',
                height: 24,
                '& .MuiChip-label': { px: 1 }
              }}
            />
          )
        )}
      </Box>

      {/* Variety display */}
      {varietyDisplay.displayText && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
          {varietyDisplay.tooltipText ? (
            <Tooltip title={varietyDisplay.tooltipText} arrow>
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{
                  cursor: 'help',
                  textDecoration: 'underline dotted',
                  fontStyle: 'italic',
                  fontSize: '0.75rem'
                }}
              >
                {varietyDisplay.displayText}
              </Typography>
            </Tooltip>
          ) : (
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{
                fontStyle: 'italic',
                fontSize: '0.75rem'
              }}
            >
              {varietyDisplay.displayText}
            </Typography>
          )}
        </Box>
      )}

      {/* Bird info grid */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr', 
        gap: 1.5,
        mb: 1.5 
      }}>
        {/* Gender and age */}
        <Box>
          {genderTerm && (
            <Typography 
              variant="body2" 
              color="primary.main"
              sx={{ 
                fontWeight: 600, 
                fontSize: '0.875rem',
                mb: 0.5
              }}
            >
              {genderTerm}
            </Typography>
          )}
          
          {/* Age display - show age or "Age unknown" */}
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: '0.8rem' }}
          >
            {ageDisplay ? `${ageDisplay} old` : 'Age unknown'}
          </Typography>
        </Box>

        {/* Weight - will be passed in separately */}
        <Box sx={{ textAlign: 'right' }}>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: '0.75rem', mb: 0.5 }}
          >
            Current Weight
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              fontWeight: 600,
              fontSize: '0.9rem',
              color: currentWeight ? 'text.primary' : 'text.secondary'
            }}
          >
            {currentWeight || 'Not recorded'}
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

export default BirdInfoSection